import { PostgrestError } from '@supabase/supabase-js';
import { debounce, isEqual } from 'radash';
import { useCallback, useEffect, useRef } from 'react';
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

export function useDeepEffect(
  effectFunc: React.EffectCallback,
  deps: unknown[],
) {
  const isFirst = useRef(true);
  const prevDeps = useRef(deps);

  useEffect(() => {
    const isSame = isEqual(prevDeps.current, deps);

    if (isFirst.current || !isSame) {
      effectFunc();
    }

    isFirst.current = false;
    prevDeps.current = deps;
  }, deps);
}

const useAutoSave = <T extends FieldValues>(
  context: UseFormReturn<T>,
  onSubmit: (data: T) => Promise<void>,
  onError?: (error: PostgrestError) => void,
) => {
  const watch = useWatch({ control: context.control });
  const hasDirtyFields = context.formState.isDirty;

  const debouncedSave = useCallback(
    debounce({ delay: 500 }, async () => {
      try {
        await context.handleSubmit(onSubmit)();
        context.reset(context.getValues(), {
          keepDirty: true,
          keepValues: true,
          keepErrors: true,
        });
      } catch (error) {
        if (error instanceof PostgrestError) {
          if (onError) {
            onError(error);
          }
        } else {
          toast.error('Unexpected error during auto-save');
          console.error(error);
        }
      }
    }),
    [context, onSubmit],
  );

  useDeepEffect(() => {
    if (hasDirtyFields) {
      debouncedSave();
    }
  }, [watch]);
};

export { useAutoSave };
